// CSRF Token Service for Frontend
// Handles CSRF token fetching and management
import { frontendLogger } from './frontendLogger.js';
import { urlConfig } from '../config/urlConfig.js';

class CSRFTokenService {
  constructor() {
    this.token = null;
    this.tokenExpiry = null;
    this.baseURL = `${urlConfig.api.baseURL}/api`;
    // Add deduplication for concurrent token requests
    this.pendingTokenRequest = null;
  }

  // Get CSRF token from server
  async getCSRFToken() {
    try {
      // Check if we have a valid cached token
      if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry) {
        return this.token;
      }

      // If there's already a pending request, return that promise
      if (this.pendingTokenRequest) {
        return await this.pendingTokenRequest;
      }

      // Create new token request and cache the promise
      this.pendingTokenRequest = this.fetchNewToken();
      
      try {
        const token = await this.pendingTokenRequest;
        return token;
      } finally {
        // Clear pending request when done (success or failure)
        this.pendingTokenRequest = null;
      }
    } catch (error) { 
      frontendLogger.error('CSRF token fetch failed', {
        component: 'CSRFService',
        action: 'getCSRFToken',
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // Separate method for the actual token fetch
  async fetchNewToken() {
    const response = await fetch(`${this.baseURL}/auth/csrf-token`, {
      method: 'GET',
      credentials: 'include', // Important: include cookies
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get CSRF token: ${response.status}`);
    }

    const data = await response.json();
    
    // Cache the token
    this.token = data.csrf_token;
    this.tokenExpiry = Date.now() + (data.expires_in * 1000); // Convert to ms
    
    return this.token;
  }

  // Clear cached token (call on logout or token error)
  clearToken() {
    this.token = null;
    this.tokenExpiry = null;
    this.pendingTokenRequest = null;
  }

  // Get auth token from localStorage (handles both user and admin tokens)
  getAuthToken() {
    // Check if we're in admin context by checking URL and admin tokens
    const isAdminPath = window.location.pathname.startsWith('/admin');
    const adminToken = localStorage.getItem('adminToken');
    
    if (isAdminPath || adminToken) {
      // We're in admin context - prioritize admin token
      return adminToken;
    }
    
    // Fallback to user tokens for regular user context
    return localStorage.getItem('sanctuary_token') || localStorage.getItem('authToken') || localStorage.getItem('token');
  }

  // Make authenticated request with CSRF token
  async makeSecureRequest(url, options = {}) {
    try {
      // Get CSRF token
      const csrfToken = await this.getCSRFToken();
      
      // Get Bearer token for authentication
      const bearerToken = this.getAuthToken();
      
      // Prepare headers with both CSRF and Bearer tokens
      const headers = {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        ...(bearerToken && { 'Authorization': `Bearer ${bearerToken}` }),
        ...options.headers
      };

      // Make request
      const response = await fetch(url, {
        ...options,
        credentials: 'include', // Include cookies
        headers
      });

      // If CSRF error, clear token and retry once
      if (response.status === 403) {
        const errorData = await response.json();
        if (errorData.code === 'CSRF_PROTECTION') {
          this.clearToken();
          
          // Retry with fresh token
          const freshToken = await this.getCSRFToken();
          const retryResponse = await fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
              ...headers,
              'X-CSRF-Token': freshToken
            }
          });

          return retryResponse;
        }
      }

      return response;
    } catch (error) { 
      frontendLogger.error('Secure request failed', {
        component: 'CSRFService',
        action: 'secureRequest',
        error: error.message,
        stack: error.stack,
        url: url,
        method: options.method || 'GET'
      });
      
      // Enhance network error handling
      if (error instanceof TypeError && 
          (error.message.includes('fetch') || 
           error.message.includes('Failed to fetch') ||
           error.message.includes('NetworkError') ||
           error.message.includes('ERR_INTERNET_DISCONNECTED') ||
           error.message.includes('ERR_NETWORK_CHANGED'))) {
        // Network error (internet down, server unreachable, etc.)
        const networkError = new Error('Unable to connect to server. Please check your internet connection or WiFi and try again. Contact support if the error persists.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      
      // Check for other common network error patterns
      if (!navigator.onLine) {
        // Browser reports offline
        const offlineError = new Error('No internet connection detected. Please check your internet connection or WiFi and try again.');
        offlineError.isNetworkError = true;
        throw offlineError;
      }
      
      // Re-throw other errors as-is
      throw error;
    }
  }

  // Helper method for POST requests
  async post(endpoint, data) {
    try {
      const fullUrl = `${this.baseURL}${endpoint}`;
      const response = await this.makeSecureRequest(fullUrl, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      // Parse JSON response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Create more descriptive error messages
        let errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        
        // Handle specific admin login errors
        if (endpoint === '/auth/admin/signin' || endpoint === '/admin/signin') {
          if (response.status === 401) {
            // Use backend message for warnings about remaining attempts
            errorMessage = errorData.error || 'Invalid email or password. Please check your credentials and try again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. Admin privileges required.';
          } else if (response.status === 429) {
            // Use backend lockout message which includes timing details
            errorMessage = errorData.error || 'Account temporarily locked due to too many failed login attempts.';
          } else if (response.status === 503) {
            // Service unavailable (backend can't reach Supabase)
            errorMessage = errorData.error || 'Authentication service temporarily unavailable. Please try again in a few moments.';
          } else if (response.status === 500) {
            errorMessage = 'Server error during login. Please try again or contact support.';
          }
        }
        
        const error = new Error(errorMessage);
        
        // Preserve validation details for better error handling
        if (errorData.details) {
          error.details = errorData.details;
        }
        
        // Attach any additional error data
        if (errorData.lockoutInfo) {
          error.lockoutInfo = errorData.lockoutInfo;
        }
        
        // Attach token info for 429 errors (rate limiting/insufficient tokens)
        if (response.status === 429 && (errorData.remaining !== undefined || errorData.used !== undefined)) {
          error.tokenInfo = {
          remaining: errorData.remaining || 0,
          used: errorData.used || 0,
          total: errorData.total || 0
        };
      }
      
      throw error;
    }
    
    return await response.json();
    
    } catch (error) {
      // Network errors are already enhanced in makeSecureRequest
      // Just re-throw them with additional context for admin login
      if (error.isNetworkError && (endpoint === '/auth/admin/signin' || endpoint === '/admin/signin')) {
        const adminNetworkError = new Error('Unable to connect to server. Please check your internet connection or WiFi and try again. Contact support if the error persists.');
        adminNetworkError.isNetworkError = true;
        throw adminNetworkError;
      }
      
      // Check if browser reports offline status
      if (!navigator.onLine && (endpoint === '/auth/admin/signin' || endpoint === '/admin/signin')) {
        const offlineError = new Error('No internet connection detected. Please check your internet connection or WiFi and try again.');
        offlineError.isNetworkError = true;
        throw offlineError;
      }
      
      throw error;
    }
  }

  // Helper method for PATCH requests
  async patch(endpoint, data) {
    const fullUrl = `${this.baseURL}${endpoint}`;
    const response = await this.makeSecureRequest(fullUrl, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    
    // Parse JSON response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      throw error;
    }
    
    return await response.json();
  }

  // Helper method for FormData POST requests (file uploads, etc.)
  async postFormData(endpoint, formData) {
    try {
      // Get CSRF token
      const csrfToken = await this.getCSRFToken();
      
      // Get Bearer token for authentication
      const bearerToken = this.getAuthToken();
      
      // Prepare headers (don't set Content-Type for FormData - browser will set it with boundary)
      const headers = {
        'X-CSRF-Token': csrfToken,
      };
      
      // Add Authorization header if we have a bearer token
      if (bearerToken) {
        headers['Authorization'] = `Bearer ${bearerToken}`;
      }
      
      const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include'
      });
      
      // Handle non-JSON error responses
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          // If response is not JSON, create a generic error
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const error = new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        
        // Handle specific error types
        if (response.status === 429) {
          error.isRateLimit = true;
        }
        
        throw error;
      }
      
      return await response.json();
      
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError = new Error('Network connection error. Please check your internet connection and try again.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      
      throw error;
    }
  }



  // Helper method for DELETE requests
  async delete(endpoint) {
    const fullUrl = `${this.baseURL}${endpoint}`;
    const response = await this.makeSecureRequest(fullUrl, {
      method: 'DELETE'
    });
    
    // Parse JSON response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      throw error;
    }
    
    return await response.json();
  }
}

// Export singleton instance
export const csrfService = new CSRFTokenService();
export default csrfService;